/**
 * Хронология ролика (вкладка «Кадры» видео-проекта, спека §9.2, v2 §1.6): горизонтальная дорожка под
 * цель, блок кадра шириной ∝ длительности (не уже 72 px) — стилл со звездой целиком (9:16,
 * `object-contain`) или пунктир «нет стилла», номер и заголовок, «−0,5 / +0,5» (поле `shot_duration_ms`
 * через `shot_field`), под блоком чипы-буквы клипов по моделям цветом статуса (K, H, S, S2…; легенда под
 * дорожкой). Блоки перетаскиваются — тот же `POST /shots/order`, что у раскадровки. В шапке панели —
 * сумма кадров «15,0 с»; против цели ролика она не сверяется (задача Оли №310: у проекта «из кадра»
 * цель производная от длительности куска, и красная строка упрекала расхождением с величиной, которой
 * сотрудник не управляет).
 *
 * Порядок и заголовки — из кадров карточки проекта (их правит раскадровка), длительность, стилл и
 * клипы — из `timeline`.
 */

import clsx from "clsx";
import { ImageUp, Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";

import { ErrorLine, Hint, Panel } from "@/components/project/ui";
import { RetryImg } from "@/components/trends/PinGrid";
import { mediaUrl } from "@/lib/media";
import { hasStill } from "@/lib/videoCompare";
import { errorText } from "@/lib/project";
import { resultPath } from "@/lib/results";
import { useShotField, useShotOrder, useVideoOptions, type TimelineShot } from "@/lib/video";
import { clipModels, modelLetters } from "@/lib/videoRun";
import {
  blockWidths,
  DURATION_STEP_MS,
  insertIndex,
  moveTo,
  nudge,
  totalOf,
  totalText,
} from "@/lib/videoTimeline";

import { Sec } from "./bits";
import { ClipChip, useModelLabels } from "./clips";
import { ShotStillPicker } from "./ShotStillPicker";

/** Длительность, которую ставит «задать» у кадра без неё: план 5 с — как «3 кадра по 5 с». */
const DEFAULT_SHOT_MS = 5000;

export function Timeline({
  pid,
  shots,
  timeline,
  allowStillPicker,
}: {
  pid: number;
  shots: readonly { id: number; shot_title: string }[];
  timeline: readonly TimelineShot[];
  /**
   * «Сменить картинку кадра» (§5/§10 спеки, видео-ролик из готового кадра) — только у проектов «из
   * кадра». У проектов «из платья» стилл выбирают звездой среди клипов нескольких моделей
   * (`generate._finish_done` ставит `picked` человек осмысленно); прямая ссылка перебила бы эту
   * отметку молча и без следа на дорожке — расширение на такие проекты не наше решение.
   */
  allowStillPicker: boolean;
}) {
  const field = useShotField(pid);
  const reorder = useShotOrder(pid);
  const label = useModelLabels();
  const options = useVideoOptions();
  const byId = useMemo(() => new Map(timeline.map((t) => [t.shot_id, t])), [timeline]);
  // Буквы — по моделям, клипы которых есть на этой хронологии (порядок справочника): K, H, S, S2…
  const present = clipModels(timeline, (options.data?.models ?? []).map((m) => m.code));
  const letters = modelLetters(present, label);

  const serverOrder = useMemo(() => shots.map((s) => s.id), [shots]);
  const serverKey = serverOrder.join(",");
  const titles = useMemo(() => new Map(shots.map((s) => [s.id, s.shot_title])), [shots]);
  const [order, setOrder] = useState<number[]>(serverOrder);
  const [dragging, setDragging] = useState<number | null>(null);
  const blocks = useRef(new Map<number, HTMLDivElement>());
  const [pickerFor, setPickerFor] = useState<number | null>(null);

  // Сервер — правда о порядке; пока тащат, правда — у руки.
  useEffect(() => {
    if (dragging === null) setOrder(serverOrder);
  }, [serverKey]);

  // Ширина дорожки — под контейнер: цель ролика занимает её целиком.
  const track = useRef<HTMLDivElement>(null);
  const [trackPx, setTrackPx] = useState(720);
  useEffect(() => {
    const el = track.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setTrackPx(Math.max(320, Math.floor(entry.contentRect.width)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const durations = order.map((id) => byId.get(id)?.duration_ms ?? null);
  // Промежутки между блоками (gap-1 = 4 px) отнимаем у дорожки, иначе кадры не влезут без прокрутки.
  const widths = blockWidths(durations, Math.max(0, trackPx - 4 * Math.max(0, order.length - 1)));
  const total = totalOf(durations);
  const busy = field.isPending ? (field.variables?.sid ?? null) : null;

  const setDuration = (sid: number, value: number) => field.mutate({ sid, field: "shot_duration_ms", value });

  const onDragOver = (x: number) => {
    if (dragging === null) return;
    const rest = order.filter((id) => id !== dragging);
    const mids = rest.map((id) => {
      const box = blocks.current.get(id)?.getBoundingClientRect();
      return box ? box.left + box.width / 2 : Number.POSITIVE_INFINITY;
    });
    const next = moveTo(order, dragging, insertIndex(mids, x));
    if (next.join(",") !== order.join(",")) setOrder(next);
  };

  const onDragEnd = () => {
    setDragging(null);
    if (order.join(",") !== serverKey) reorder.mutate(order, { onError: () => setOrder(serverOrder) });
  };

  return (
    <Panel
      title="Хронология"
      aside={
        <span className="text-[13px] font-medium whitespace-nowrap text-fg" title="сумма длительностей кадров">
          <span className="tabular-nums">{totalText(total.sumMs)}</span> с
        </span>
      }
      annot="хронология ролика"
    >
      <Hint>
        Ширина блока — длительность кадра; блоки перетаскиваются. Буквы под блоком — клипы по моделям, цвет —
        статус (наведите — модель, статус, цена).
      </Hint>
      <ErrorLine text={field.isError ? `Длительность не сохранилась: ${errorText(field.error)}` : ""} className="mt-2" />
      <ErrorLine text={reorder.isError ? `Порядок не сохранился: ${errorText(reorder.error)}` : ""} className="mt-2" />

      <div ref={track} className="mt-3 overflow-x-auto pb-1">
        {order.length === 0 ? (
          <p className="text-[12.5px] text-fg-faint italic">Кадров пока нет — примените сценарий или добавьте кадр.</p>
        ) : (
          <div
            className="flex items-start gap-1"
            onDragOver={(e) => {
              if (dragging === null) return;
              e.preventDefault();
              onDragOver(e.clientX);
            }}
          >
            {order.map((id, n) => {
              const shot = byId.get(id);
              const ms = shot?.duration_ms ?? null;
              const down = nudge(ms, -DURATION_STEP_MS);
              const up = nudge(ms, DURATION_STEP_MS);
              const locked = busy === id;
              return (
                <div
                  key={id}
                  ref={(el) => {
                    if (el) blocks.current.set(id, el);
                    return () => {
                      blocks.current.delete(id);
                    };
                  }}
                  data-annot={`блок кадра ${id}`}
                  draggable
                  onDragStart={(e) => {
                    setDragging(id);
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", String(id));
                  }}
                  onDragEnd={onDragEnd}
                  style={{ width: widths[n] }}
                  className={clsx(
                    "flex-none cursor-grab rounded-[var(--radius-ui)] border bg-bg p-1 transition-opacity active:cursor-grabbing",
                    dragging === id ? "border-accent opacity-50" : "border-border",
                  )}
                >
                  <div className="relative h-[128px] w-full overflow-hidden rounded-[3px] bg-bg-subtle">
                    {shot?.still && hasStill(shot.still) ? (
                      <Link to={resultPath(shot.still.variant_id)} title="карточка стилла" draggable={false}>
                        <RetryImg
                          src={mediaUrl(shot.still.asset_path)}
                          alt=""
                          draggable={false}
                          className="h-full w-full object-contain"
                        />
                      </Link>
                    ) : (
                      <div className="grid h-full w-full place-items-center border border-dashed border-border-strong text-center text-[10.5px] leading-tight text-fg-faint">
                        нет стилла
                      </div>
                    )}
                    {allowStillPicker ? (
                      <button
                        type="button"
                        title="сменить картинку кадра"
                        aria-label="сменить картинку кадра"
                        draggable={false}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPickerFor(id);
                        }}
                        className="absolute top-1 right-1 grid size-5 place-items-center rounded-[4px] border border-white/80 bg-black/55 text-white hover:bg-black/75"
                      >
                        <ImageUp size={12} />
                      </button>
                    ) : null}
                  </div>
                  <Link
                    to={`/project/${pid}/shot/${id}`}
                    draggable={false}
                    title={titles.get(id) ?? shot?.title ?? ""}
                    className="mt-1 block truncate text-[11.5px] text-fg hover:text-accent-text"
                  >
                    <b className="tnum">{n + 1}</b> {titles.get(id) ?? shot?.title ?? ""}
                  </Link>
                  <div className="mt-0.5 flex items-center gap-0.5">
                    {ms === null ? (
                      <button
                        type="button"
                        disabled={locked}
                        onClick={() => setDuration(id, DEFAULT_SHOT_MS)}
                        title="у кадра нет длительности — клип не запустится"
                        className="h-5 rounded-[3px] px-1 text-[11px] text-warn hover:bg-bg-hover disabled:opacity-50"
                      >
                        задать 5 с
                      </button>
                    ) : (
                      <>
                        <NudgeBtn
                          title="−0,5 с"
                          disabled={locked || down === null}
                          onClick={() => down !== null && setDuration(id, down)}
                        >
                          <Minus size={11} />
                        </NudgeBtn>
                        <Sec ms={ms} className={clsx("min-w-0 flex-1 text-center text-[11.5px]", locked && "opacity-50")} />
                        <NudgeBtn
                          title="+0,5 с"
                          disabled={locked || up === null}
                          onClick={() => up !== null && setDuration(id, up)}
                        >
                          <Plus size={11} />
                        </NudgeBtn>
                      </>
                    )}
                  </div>
                  {shot?.clips.length ? (
                    <div className="mt-1 flex flex-wrap gap-1 px-0.5">
                      {shot.clips.map((c) => (
                        <ClipChip
                          key={c.generation_id}
                          clip={c}
                          label={label(c.model)}
                          letter={letters.get(c.model) ?? "?"}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {present.length ? (
        <Hint className="mt-1">
          {present.map((code) => `${letters.get(code)} — ${label(code)}`).join(" · ")}
        </Hint>
      ) : null}
      {allowStillPicker ? (
        <ShotStillPicker
          open={pickerFor !== null}
          onClose={() => setPickerFor(null)}
          pid={pid}
          shotId={pickerFor ?? 0}
          currentVariantId={byId.get(pickerFor ?? -1)?.still?.variant_id ?? null}
        />
      ) : null}
    </Panel>
  );
}

function NudgeBtn({
  title,
  disabled,
  onClick,
  children,
}: {
  title: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className="grid size-5 flex-none place-items-center rounded-[3px] border border-border text-fg-muted hover:border-border-strong hover:bg-bg-hover hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
